// Pro devis / commande PDFs, rendered with the SHARED "Tunisien" document
// design from @erp/pdf (the same model the ERP app uses), branded with the pro
// space's own logo + primary color.
//
// The document MODEL (lines, VAT rates, notes, template/options resolution)
// lives in @erp/pdf `pro-render-data` and is shared with the pro MOBILE app
// (apps/tunielec-pro-mobile), which renders the same model through
// render-html + expo-print. This file only owns the web output mechanics.
//
// @erp/pdf (which bundles pdfmake + fonts, ~700KB) is dynamically imported so it
// only loads when the user actually clicks Télécharger.
//
// API:
//   await downloadDevisPdf(devis, lookupArticle, branding, fileName?)
//   await downloadCommandePdf(cmd, lookupArticle, branding, fileName?)
//   await printDevisPdf(devis, lookupArticle, branding)
//   await printCommandePdf(cmd, lookupArticle, branding)

import type {
  ProDevis,
  ProCommande,
  PublicProPdfTemplate,
} from '@erp/data-access/interfaces';
import type { PdfCompanyInfo, RenderData } from '@erp/pdf';
import {
  buildProDevisRenderData,
  buildProCommandeRenderData,
  resolveProPdfTemplate,
  proDevisFileName,
  proCommandeFileName,
  PRO_DEVIS_DOC_TITLE,
  PRO_COMMANDE_DOC_TITLE,
  type ProArticleLookup,
} from '@erp/pdf/pro-render-data';

/** Company identity + brand color the pro portal feeds into the PDF design.
 *  Sourced from the tenant + store theme via `useProBranding().pdfCompany`. */
export interface ProPdfBranding {
  company: PdfCompanyInfo;
  primaryColor?: string | null;
  /** The tenant's Espace Pro PDF model (Réglages → Modèles PDF). When set it
   *  wins entirely — design AND options, exactly what the settings editor
   *  previewed. Null/undefined keeps the historical built-in: Tunisien tinted
   *  with the pro theme's primary color. */
  template?: PublicProPdfTemplate | null;
}

type ArticleLookup = (
  articleId: string,
) => { name: string; category?: string } | null;

/** The (design, options, company, data) tuple both outputs share. */
function generateInput(data: RenderData, docTypeTitle: string, branding: ProPdfBranding) {
  const { designKey, options } = resolveProPdfTemplate({
    template: branding.template,
    primaryColor: branding.primaryColor,
  });
  return { designKey, options, company: branding.company, data, docTypeTitle };
}

/** Build a Blob via @erp/pdf and trigger a browser download (keeps the pro
 *  "Télécharger" UX rather than opening a print dialog). */
async function downloadRenderData(
  data: RenderData,
  docTypeTitle: string,
  branding: ProPdfBranding,
  fileName: string,
): Promise<void> {
  const { generatePdfBlob } = await import('@erp/pdf');
  const blob = await generatePdfBlob(generateInput(data, docTypeTitle, branding));
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Same PDF, sent to the printer instead of the disk. Goes through @erp/pdf's
 *  output seam (hidden iframe on the web, native bridge in the desktop shell) —
 *  NOT `window.print()`, which would print the web page. */
async function printRenderData(
  data: RenderData,
  docTypeTitle: string,
  branding: ProPdfBranding,
): Promise<void> {
  const { generatePdf } = await import('@erp/pdf');
  await generatePdf(generateInput(data, docTypeTitle, branding));
}

export async function downloadDevisPdf(
  devis: ProDevis,
  lookupArticle: ArticleLookup,
  branding: ProPdfBranding,
  fileName?: string,
): Promise<void> {
  const data = buildProDevisRenderData(devis, lookupArticle as ProArticleLookup);
  await downloadRenderData(
    data,
    PRO_DEVIS_DOC_TITLE,
    branding,
    fileName ?? proDevisFileName(devis.code),
  );
}

export async function downloadCommandePdf(
  cmd: ProCommande,
  lookupArticle: ArticleLookup,
  branding: ProPdfBranding,
  fileName?: string,
): Promise<void> {
  const data = buildProCommandeRenderData(cmd, lookupArticle as ProArticleLookup);
  await downloadRenderData(
    data,
    PRO_COMMANDE_DOC_TITLE,
    branding,
    fileName ?? proCommandeFileName(cmd.code),
  );
}

export async function printDevisPdf(
  devis: ProDevis,
  lookupArticle: ArticleLookup,
  branding: ProPdfBranding,
): Promise<void> {
  const data = buildProDevisRenderData(devis, lookupArticle as ProArticleLookup);
  await printRenderData(data, PRO_DEVIS_DOC_TITLE, branding);
}

export async function printCommandePdf(
  cmd: ProCommande,
  lookupArticle: ArticleLookup,
  branding: ProPdfBranding,
): Promise<void> {
  const data = buildProCommandeRenderData(cmd, lookupArticle as ProArticleLookup);
  await printRenderData(data, PRO_COMMANDE_DOC_TITLE, branding);
}
